import { useMantineTheme } from '@mantine/core';
import { Flex } from '@mantine/core';
import { runInAction, when } from 'mobx';
import moment from 'moment-timezone';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OptionProps } from 'react-select';
import { components } from 'react-select';

import type { Form } from '@feathr/blackbox';
import type { IFormElementProps, ISelectOption, ISelectProps } from '@feathr/components';
import { isMulti, Select, Text } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { getIconForAction, TimeFormat } from '@feathr/hooks';

export interface IFormSelectBaseProps extends IFormElementProps {
  filters?: Record<string, unknown>;
  isLoading?: boolean;
  placeholder?: ISelectProps['placeholder'];
  showLastModified?: boolean;
  wrapperClassName?: ISelectProps['wrapperClassName'];
}

export interface IFormSelectSingleProps extends IFormSelectBaseProps {
  isMulti?: false;
  onChange: (value?: string) => void;
  value?: string;
}

export interface IFormSelectMultiProps extends IFormSelectBaseProps {
  isMulti: true;
  onChange: (value?: string[]) => void;
  value?: string[];
}

interface IFormOption extends ISelectOption {
  date_last_modified: string;
}

function FormOption(props: OptionProps<IFormOption>): JSX.Element {
  const { t } = useTranslation();
  const { data } = props;
  const {
    spacing: { '1': gap },
  } = useMantineTheme();
  return (
    <components.Option {...props}>
      <Flex direction={'column'} gap={gap}>
        <Text.DefaultHeadingMedium>{data.name}</Text.DefaultHeadingMedium>
        <Text.DefaultRegular>
          {t('Last modified {{- date}}', {
            date: moment.utc(data.date_last_modified).format(TimeFormat.longDate),
          })}
        </Text.DefaultRegular>
      </Flex>
    </components.Option>
  );
}

function FormSelect(props: Readonly<IFormSelectSingleProps>): JSX.Element;
function FormSelect(props: Readonly<IFormSelectMultiProps>): JSX.Element;
function FormSelect(props: Readonly<IFormSelectSingleProps | IFormSelectMultiProps>): JSX.Element {
  const { Forms } = useStore();
  const [forms, setForms] = useState<Form[]>();
  const [isPending, setIsPending] = useState<boolean>(false);

  const { filters, isLoading, showLastModified = false } = props;
  useEffect(() => {
    (async function fetchForms(): Promise<void> {
      setIsPending(true);
      const fetchedForms = await Forms.list({ filters });
      await when(() => !fetchedForms.isPending);

      // Use runInAction to batch state updates
      runInAction(() => {
        setForms(fetchedForms.models);
        setIsPending(false);
      });
    })();
  }, [Forms, filters]);

  const options: ISelectOption[] =
    forms?.map((form) => ({
      id: form.id,
      name: form.name,
      date_last_modified: form.get('date_last_modified'),
    })) ?? [];

  const baseProps = {
    name: 'form-select',
    options,
    prefix: getIconForAction('search'),
    ...(showLastModified && {
      components: {
        Option: FormOption,
      },
    }),
  };

  if (isMulti(props)) {
    const { onChange, value, ...rest } = props;

    function handleChange(option: readonly ISelectOption[]): void {
      onChange(option?.map((o) => o.id));
    }

    const selectedOptions = options.filter((option) => {
      return value?.includes(option.id);
    });

    return (
      <Select<ISelectOption>
        {...baseProps}
        {...rest}
        isLoading={isPending || isLoading}
        onChange={handleChange}
        value={selectedOptions}
      />
    );
  } else {
    const { onChange, value, ...rest } = props;

    function handleChange(option: ISelectOption): void {
      onChange(option?.id);
    }

    const selectedOption = options.find((option) => {
      return value === option.id;
    });

    return (
      <Select<ISelectOption>
        {...baseProps}
        {...rest}
        isLoading={isPending || isLoading}
        onChange={handleChange}
        value={selectedOption}
      />
    );
  }
}

export default FormSelect;
